import * as React from 'react';
import { cn } from '../../lib/utils.js';

/**
 * Textarea — multi-line text input primitive.
 *
 * Slice 1 had no textarea because no form needed it (catalog
 * forms are short). Slice 2 PR#3 introduces notes fields on
 * receipts, quotations, and orders, so we ship the primitive.
 *
 * Style parity with `Input`: same border + focus ring, same
 * `aria-invalid` semantics. Forwarded refs via `React.forwardRef`
 * so a parent form can still grab the underlying textarea.
 */
export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(
          'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors',
          'placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-[invalid=true]:border-destructive',
          className,
        )}
        {...props}
      />
    );
  },
);

Textarea.displayName = 'Textarea';
